type DBConfig = { dbName: string; storeName: string; version?: number; keyPath?: string };
type IDBKey = IDBValidKey;

// Cache for database connections
const dbCache: Map<string, Promise<IDBDatabase>> = new Map();

const buildCacheKey = (config: DBConfig) => {
  return `${config.dbName}-${config.storeName}-${config.version || 1}`;
};

const openDB = async (config: DBConfig): Promise<IDBDatabase> => {
  const { dbName, version = 1, storeName, keyPath } = config;

  const cacheKey = buildCacheKey(config);

  if (!dbCache.has(cacheKey)) {
    await cleanUpDbs();

    console.log("Opening database: ", config.dbName);
    dbCache.set(
      cacheKey,
      new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, version);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath, autoIncrement: true });
          }
        };

        request.onsuccess = (event) => resolve((event.target as IDBOpenDBRequest).result);
        request.onerror = () => reject(request.error);
      })
    );
  }

  console.log("Opening cached database: ", config.dbName);
  return dbCache.get(cacheKey)!;
};

const performTransaction = <T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = operation(store);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const addItem = async <T>(config: DBConfig, data: T): Promise<IDBKey> => {
  const db = await openDB(config);
  return performTransaction(db, config.storeName, "readwrite", (store) => store.add(data));
};

const addItems = async <T>(config: DBConfig, dataArray: T[]): Promise<IDBKey[]> => {
  const db = await openDB(config);

  return new Promise<IDBKey[]>((resolve, reject) => {
    const tx = db.transaction(config.storeName, "readwrite");
    const store = tx.objectStore(config.storeName);

    const results: IDBKey[] = [];
    let completed = 0;

    dataArray.forEach((item, index) => {
      const request = store.add(item);
      request.onsuccess = () => {
        results[index] = request.result;
        completed++;
        if (completed === dataArray.length) {
          // Wait for full transaction to commit
          tx.oncomplete = () => resolve(results);
        }
      };
      request.onerror = () => reject(request.error);
    });

    tx.onerror = () => reject(tx.error);
  });
};

const getItem = async <T>(config: DBConfig, id: IDBKey): Promise<T | undefined> => {
  const db = await openDB(config);
  return performTransaction(db, config.storeName, "readonly", (store) => store.get(id));
};

const getAllItems = async <T>(config: DBConfig): Promise<T[]> => {
  const db = await openDB(config);
  return performTransaction(db, config.storeName, "readonly", (store) => store.getAll());
};

const getAllItemsBatched = async <T>(
  config: DBConfig,
  processBatch: (items: T[]) => Promise<void>,
  batchSize = 100
): Promise<void> => {
  const db = await openDB(config);

  // Keep track of how many items we've processed
  let hasMoreItems = true;
  let lastCursorPosition: IDBValidKey | undefined = undefined;

  while (hasMoreItems) {
    const batch: T[] = [];

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(config.storeName, "readonly");
      const store = transaction.objectStore(config.storeName);

      // Use a cursor to iterate through items
      const cursorRequest = lastCursorPosition
        ? store.openCursor(IDBKeyRange.lowerBound(lastCursorPosition, true))
        : store.openCursor();

      let itemsInBatch = 0;

      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;

        if (cursor && itemsInBatch < batchSize) {
          // Add item to current batch
          batch.push(cursor.value);
          itemsInBatch++;

          // Keep track of the last key we've seen for the next batch
          lastCursorPosition = cursor.key;

          // Move to the next item
          cursor.continue();
        } else {
          // Either no more items or batch is full
          hasMoreItems = cursor !== null;
          resolve();
        }
      };

      cursorRequest.onerror = () => reject(cursorRequest.error);
      transaction.onerror = () => reject(transaction.error);
    });

    // Process the collected batch
    if (batch.length > 0) {
      console.log(`Processed ${batch.length} assets`);
      await processBatch(batch);
    }
  }
};

const updateItem = async <T extends { id: IDBKey }>(config: DBConfig, data: T): Promise<void> => {
  const db = await openDB(config);
  await performTransaction(db, config.storeName, "readwrite", (store) => store.put(data));
};

const deleteItem = async (config: DBConfig, id: IDBKey): Promise<void> => {
  const db = await openDB(config);
  await performTransaction(db, config.storeName, "readwrite", (store) => store.delete(id));
};

const clearStore = async (config: DBConfig): Promise<void> => {
  const db = await openDB(config);
  await performTransaction(db, config.storeName, "readwrite", (store) => store.clear());
  console.log(`All items in store "${config.storeName}" have been deleted.`);
};

const closeDatabase = async (config: DBConfig): Promise<void> => {
  const cacheKey = buildCacheKey(config);
  const db = await openDB(config);
  db.close();
  dbCache.delete(cacheKey);
  console.log(`Closed database: ${config.dbName}`);

  await new Promise<void>((resolve, reject) => {
    const deleteRequest = indexedDB.deleteDatabase(config.dbName);

    deleteRequest.onsuccess = () => {
      console.log(`Deleted database: ${config.dbName}`);
      resolve();
    };

    deleteRequest.onerror = () => {
      console.error(`Failed to delete database: ${config.dbName}`, deleteRequest.error);
      reject(deleteRequest.error);
    };

    deleteRequest.onblocked = () => {
      console.warn(`Delete blocked: please close all tabs using the database "${config.dbName}"`);
    };
  });
};

const cleanUpDbs = async (): Promise<void> => {
  try {
    const databases = await indexedDB.databases();

    if (!databases.length) {
      return;
    }

    for (const db of databases) {
      if (!db.name) continue;

      try {
        const deleteRequest = indexedDB.deleteDatabase(db.name);
        deleteRequest.onsuccess = () => console.log(`Deleted database: "${db.name}"`);
        deleteRequest.onerror = () => console.error(`Failed to delete "${db.name}":`, deleteRequest.error);
        deleteRequest.onblocked = () => console.warn(`Deletion of "${db.name}" is blocked.`);
      } catch (e) {
        console.error(`Error deleting "${db.name}":`, e);
      }
    }
  } catch (err) {
    console.error("Error listing databases:", err);
  }
};

const getCount = async (config: DBConfig): Promise<{ count: number }> => {
  const db = await openDB(config);
  const count = await performTransaction(db, config.storeName, "readonly", (store) => store.count());
  return { count };
};
const getItems = async <T>(config: DBConfig, offset: number, limit: number): Promise<T[]> => {
  const db = await openDB(config);

  return new Promise<T[]>((resolve, reject) => {
    const results: T[] = [];
    const transaction = db.transaction(config.storeName, "readonly");
    const store = transaction.objectStore(config.storeName);
    const request = store.openCursor();

    let skipped = 0;
    let added = 0;

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (!cursor || added >= limit) {
        resolve(results);
        return;
      }

      if (skipped < offset) {
        skipped++;
        cursor.continue();
        return;
      }

      results.push(cursor.value);
      added++;
      cursor.continue();
    };

    request.onerror = () => reject(request.error);
    transaction.onerror = () => reject(transaction.error);
  });
};

export const IndexedDb = {
  openDB,
  addItem,
  addItems,
  getItem,
  getAllItems,
  getAllItemsBatched,
  updateItem,
  deleteItem,
  clearStore,
  closeDatabase,
  getCount,
  getItems,
};
